import React from "react";

import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";
import { useUpdateEffect } from "react-use";
import { Button } from "@mui/material";

import { sanitizeHtml } from "@/core/security";
import {
    FORBIDDEN,
    ForbiddenError,
    isSupportedErrorCode,
    SupportedErrorCode,
    TIMEOUT,
    TimeoutError,
} from "@/shared/errors";
import { useNavigation, xCorrelationIdProvider } from "@/shared/hooks";

import { StyledErrorCard, StyledErrorInfo, StyledErrorTitle, StyledErrorWrapper } from "./styled";

export type ErrorCardProps<ErrorType> = {
    refreshButton?: boolean;
    error?: ErrorType;
};

const ErrorCard = <ErrorType extends Error & { code?: string }>({
    error,
    refreshButton = false,
}: ErrorCardProps<ErrorType>): React.ReactElement => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const { refreshPage } = useNavigation();
    const xCorrelationId = xCorrelationIdProvider.getXCorrelationId();

    useUpdateEffect(() => {
        if (error) {
            queryClient.resetQueries();
        }
    }, [error]);

    let errorTitle = t("Errors.Title");
    let errorMessage = t("Errors.Code");

    const getErrorCode = (): SupportedErrorCode => {
        if (error instanceof ForbiddenError) {
            return FORBIDDEN;
        }
        if (error instanceof TimeoutError) {
            return TIMEOUT;
        }
        if (error.code && isSupportedErrorCode(error.code)) {
            return error.code;
        }
        return undefined;
    };

    if (error) {
        const code = getErrorCode();
        if (code) {
            errorTitle = t("Errors.Title", { context: code });
            errorMessage = t("Errors.Code", { context: code });
        }
    }

    return (
        <StyledErrorCard>
            <StyledErrorWrapper>
                <StyledErrorTitle>{errorTitle}</StyledErrorTitle>
                <StyledErrorInfo dangerouslySetInnerHTML={{ __html: sanitizeHtml(errorMessage) }} />
                <StyledErrorInfo>
                    {t("Errors.ErrorId")}: {xCorrelationId}
                </StyledErrorInfo>
                {refreshButton && (
                    <Button fullWidth data-testid="button-refresh" onClick={refreshPage}>
                        {t("Common.TryAgain")}
                    </Button>
                )}
            </StyledErrorWrapper>
        </StyledErrorCard>
    );
};

export default ErrorCard;
